/**
 * Sub-page 4: match rules.
 *
 * A rule is a name, the fields it compares, and what a match is *for*.
 *
 * Each field is compared one of two ways. `exact` answers yes or no; `fuzzy`
 * scores similarity. The moment any field is fuzzy the rule can only be
 * relevance-based — a fractional score is the only thing it produces — so the
 * other two actions disappear and the two relevance bars take over.
 *
 * Which records are even worth comparing is derived from the fields on the
 * server rather than configured here: a blocking list that does not follow the
 * fields is how a rule silently stops firing.
 */

import { useMemo, useState } from 'react'
import { EmptyState, Modal } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Field, SectionHeader, ThresholdBar } from '../../components'
import { RelevanceTrack, type Spans } from '../../components/RelevanceTrack'
import { ReorderableList } from '../../components/ReorderableList'
import { useMatchMutations } from '../../hooks'
import type {
  MatchAction,
  MatchField,
  MatchMethod,
  MatchRule,
  MatchTestResult,
  NodeTypeDetail,
} from '../../types/brain.types'

const METHODS: { method: MatchMethod; label: string; summary: string }[] = [
  {
    method: 'exact',
    label: 'Exact',
    summary: 'The values agree, ignoring case and surrounding spaces.',
  },
  {
    method: 'fuzzy',
    label: 'Fuzzy',
    summary: 'The values are scored for similarity, ignoring word order and company suffixes.',
  },
]

/** What a match is for. `relevance` needs a score, so it is fuzzy-only. */
const ACTIONS: { action: MatchAction; label: string; summary: string; icon: string }[] = [
  {
    action: 'auto_merge',
    label: 'Automatically merge',
    summary: 'Fold the pair into one record.',
    icon: '⇉',
  },
  {
    action: 'review',
    label: 'Flag for review',
    summary: 'Send the pair to the queue for a person.',
    icon: '⚑',
  },
  {
    action: 'relevance',
    label: 'Relevance based',
    summary: 'Let the two bars below decide.',
    icon: '％',
  },
]

const ACTION_LABEL: Record<MatchAction, string> = {
  auto_merge: 'Automatically merge',
  review: 'Flag for review',
  relevance: 'Relevance based',
}

const hasFuzzy = (fields: { method: MatchMethod }[]) => fields.some((f) => f.method === 'fuzzy')

export function MatchPanel({
  graphId,
  nodeTypeId,
  detail,
}: {
  graphId: string
  nodeTypeId: string
  detail: NodeTypeDetail
}) {
  const { remove, update, reorder, test } = useMatchMutations(graphId, nodeTypeId)
  const toast = useToast()

  const [editing, setEditing] = useState<MatchRule | 'new' | null>(null)
  const [results, setResults] = useState<MatchTestResult[] | null>(null)

  return (
    <div className="stack">
      <SectionHeader
        title="Match rules"
        hint="How two records are recognised as the same thing, tried in the order below. The first rule to reach a verdict decides, so a rule's position is part of what it does. A rule names the fields it compares and whether each has to agree exactly or only closely; a rule with any fuzzy field is decided by relevance."
        action={
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            Add match rule
          </button>
        }
      />

      {detail.match_rules.length === 0 ? (
        <EmptyState
          title="No match rules yet"
          action={
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              Add the first rule
            </button>
          }
        >
          Every incoming record becomes its own new record. That is safe, but the same customer
          arriving from two systems will sit in the graph twice.
        </EmptyState>
      ) : (
        <ReorderableList
          items={detail.match_rules.map((rule) => ({
            ...rule,
            id: rule.match_rule_id,
            // The primary-key rule stays at position 1. It is the strongest
            // statement the model makes about identity, and a rule that ran
            // after a fuzzy one would never get to make it.
            locked: rule.is_default,
          }))}
          disabled={reorder.isPending}
          onReorder={(orderedIds) =>
            reorder.mutateAsync(orderedIds).catch((error: unknown) => toast.error(error))
          }
          renderItem={(rule) => (
          <div className="rulecard" data-off={!rule.active}>
            <div className="cp-rulehdr">
              <span
                className="cp-ruleicon"
                data-tone={rule.is_fuzzy ? 'warn' : 'ok'}
                aria-hidden="true"
              >
                {rule.is_fuzzy ? '≈' : '='}
              </span>
              <span className="cp-ruletitle">
                <b>{rule.name}</b>
                <span>
                  {rule.is_fuzzy ? 'fuzzy' : 'exact'} · {rule.fields.length} field
                  {rule.fields.length === 1 ? '' : 's'} · {ACTION_LABEL[rule.action]}
                </span>
              </span>
              {!rule.active && <span className="badge badge-neutral">disabled</span>}
              <button
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  update
                    .mutateAsync({ id: rule.match_rule_id, body: { active: !rule.active } })
                    .catch((error) => toast.error(error))
                }
              >
                {rule.active ? 'Disable' : 'Enable'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(rule)}>
                Edit
              </button>
              {!rule.is_default && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={async () => {
                  if (!window.confirm(`Delete '${rule.name}'?`)) return
                  try {
                    await remove.mutateAsync(rule.match_rule_id)
                    toast.success('Rule deleted.')
                  } catch (error) {
                    toast.error(error)
                  }
                }}
              >
                Delete
              </button>
              )}
              {rule.is_default && (
                <span
                  className="badge badge-neutral"
                  title="Created from this node type's primary key, and kept in step with it."
                >
                  from the primary key
                </span>
              )}
            </div>

            <div className="cp-dcard">
              <div className="cp-dctitle">Fields compared</div>
              {rule.fields.map((field) => (
                <div className="cp-sig" key={field.property_key}>
                  <span className="nm mono">{field.property_key}</span>
                  <span
                    className={`badge ${field.method === 'fuzzy' ? 'badge-warn' : 'badge-ok'}`}
                  >
                    {field.method}
                  </span>
                </div>
              ))}

              {rule.blocking_keys.length === 0 && (
                <p className="small">
                  <span className="badge badge-warn">this rule never fires</span> It compares no
                  fields, so no pair is ever considered.
                </p>
              )}
            </div>

            <div className="cp-dcard">
              <div className="cp-dctitle">
                {rule.action === 'relevance' ? 'Relevance' : 'What happens on a match'}
              </div>

              {rule.action === 'relevance' ? (
                <>
                  <ThresholdBar
                    review={rule.review_threshold}
                    reviewMax={rule.review_max}
                    autoMerge={rule.auto_merge_threshold}
                    autoMergeMax={rule.auto_merge_max}
                  />
                  <div className="cp-outcome" style={{ marginTop: 14 }}>
                    <span className="lbl">
                      Scores from {rule.auto_merge_threshold.toFixed(2)} to{' '}
                      {rule.auto_merge_max.toFixed(2)} merge with nobody watching; from{' '}
                      {rule.review_threshold.toFixed(2)} to {rule.review_max.toFixed(2)} they
                      wait in the review queue. Where the two overlap, merging wins; anything
                      else stays separate.
                    </span>
                    <button className="btn btn-sm" onClick={() => setEditing(rule)}>
                      Adjust
                    </button>
                  </div>
                </>
              ) : (
                <div className="cp-outcome">
                  <span className="lbl">
                    {rule.action === 'auto_merge'
                      ? 'Every matching pair is merged with nobody watching.'
                      : 'Every matching pair waits in the review queue for a person.'}
                  </span>
                  <button className="btn btn-sm" onClick={() => setEditing(rule)}>
                    Change
                  </button>
                </div>
              )}
            </div>
          </div>
          )}
        />
      )}

      {detail.match_rules.length > 0 && (
        <PairTester
          detail={detail}
          results={results}
          busy={test.isPending}
          onRun={async (left, right) => {
            try {
              setResults(await test.mutateAsync({ left, right }))
            } catch (error) {
              toast.error(error)
            }
          }}
        />
      )}

      {editing && (
        <MatchRuleDialog
          graphId={graphId}
          nodeTypeId={nodeTypeId}
          detail={detail}
          existing={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

const VERDICT_LABEL = {
  auto_merge: { text: 'Merges automatically', cls: 'badge-ok' },
  review: { text: 'Goes to review', cls: 'badge-warn' },
  distinct: { text: 'Stays separate', cls: 'badge-neutral' },
} as const

function PairTester({
  detail,
  results,
  busy,
  onRun,
}: {
  detail: NodeTypeDetail
  results: MatchTestResult[] | null
  busy: boolean
  onRun: (left: Record<string, unknown>, right: Record<string, unknown>) => void
}) {
  const [left, setLeft] = useState<Record<string, string>>({})
  const [right, setRight] = useState<Record<string, string>>({})

  const fields = detail.properties.slice(0, 8)

  return (
    <div className="card stack">
      <SectionHeader
        title="Try two records"
        hint="Score a pair against every rule on this node type, and see which field moved the number. This is the same comparison the pipeline makes between an incoming record and the ones already in the graph. Nothing is saved."
      />

      <div className="diffpair">
        <div className="stack" style={{ gap: 6 }}>
          <span className="small faint">Record A — the incoming one</span>
          {fields.map((property) => (
            <input
              key={property.property_id}
              className="input"
              placeholder={property.key}
              value={left[property.key] ?? ''}
              onChange={(e) => setLeft((v) => ({ ...v, [property.key]: e.target.value }))}
            />
          ))}
        </div>
        <div className="stack" style={{ gap: 6 }}>
          <span className="small faint">Record B — one already in the graph</span>
          {fields.map((property) => (
            <input
              key={property.property_id}
              className="input"
              placeholder={property.key}
              value={right[property.key] ?? ''}
              onChange={(e) => setRight((v) => ({ ...v, [property.key]: e.target.value }))}
            />
          ))}
        </div>
      </div>

      <button
        className="btn btn-primary"
        disabled={busy}
        onClick={() =>
          onRun(
            Object.fromEntries(Object.entries(left).filter(([, v]) => v !== '')),
            Object.fromEntries(Object.entries(right).filter(([, v]) => v !== '')),
          )
        }
      >
        {busy ? 'Scoring…' : 'Score the pair'}
      </button>

      {results?.map((result) => {
        const verdict = VERDICT_LABEL[result.verdict]
        const compared =
          result.blocks_left.length > 0 &&
          result.blocks_right.length > 0 &&
          result.blocks_left.some((b) => result.blocks_right.includes(b))
        return (
          <div key={result.match_rule_id} className="stack" style={{ gap: 6 }}>
            <div className="row-tight">
              <strong>{result.name}</strong>
              <span className={`badge ${verdict.cls}`}>{verdict.text}</span>
              <span className="mono small">{result.score.toFixed(3)}</span>
              <span className="badge badge-neutral">{ACTION_LABEL[result.action]}</span>
              {!compared && (
                <span className="badge badge-warn">these two would never be compared</span>
              )}
            </div>
            {result.action === 'relevance' && (
              <ThresholdBar
                review={result.review_threshold}
                reviewMax={result.review_max}
                autoMerge={result.auto_merge_threshold}
                autoMergeMax={result.auto_merge_max}
                score={result.score}
              />
            )}
            {Object.entries(result.signals).map(([key, signal]) => (
              <div className="row-tight small" key={key}>
                <span className="mono" style={{ minWidth: 140 }}>
                  {key}
                </span>
                <span className="faint">{signal.method}</span>
                <span className="muted">
                  {signal.reason ??
                    (typeof signal.value === 'number'
                      ? signal.value.toFixed(3)
                      : String(signal.value))}
                </span>
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}

function MatchRuleDialog({
  graphId,
  nodeTypeId,
  detail,
  existing,
  onClose,
}: {
  graphId: string
  nodeTypeId: string
  detail: NodeTypeDetail
  existing?: MatchRule
  onClose: () => void
}) {
  const { create, update } = useMatchMutations(graphId, nodeTypeId)
  const toast = useToast()

  const firstProperty = detail.properties[0]?.key ?? ''

  const [name, setName] = useState(existing?.name ?? '')
  const [fields, setFields] = useState<MatchField[]>(
    existing?.fields?.length
      ? existing.fields.map((f) => ({ ...f }))
      : [{ property_key: firstProperty, method: 'exact' }],
  )
  const [action, setAction] = useState<MatchAction>(existing?.action ?? 'relevance')
  const [spans, setSpans] = useState<Spans>({
    autoMerge: existing?.auto_merge_threshold ?? 0.9,
    autoMergeMax: existing?.auto_merge_max ?? 1,
    review: existing?.review_threshold ?? 0.7,
    reviewMax: existing?.review_max ?? 0.9,
  })

  const fuzzy = hasFuzzy(fields)

  // What a rule can mean follows from how it compares. Any fuzzy field (on its
  // own or beside exact ones) yields a score, and only relevance can read a
  // score — so that is the one option. All-exact fields yield a yes or no, so
  // the choice is what to do with a yes: merge, or ask a person. The effective
  // action is computed rather than trusted, because the state can predate a
  // change to the fields.
  const effectiveAction: MatchAction = fuzzy
    ? 'relevance'
    : action === 'relevance'
      ? 'review'
      : action
  const offeredActions = fuzzy
    ? ACTIONS.filter((a) => a.action === 'relevance')
    : ACTIONS.filter((a) => a.action !== 'relevance')

  const duplicate = useMemo(() => {
    const seen = new Set<string>()
    return fields.some((f) => {
      if (!f.property_key || seen.has(f.property_key)) return true
      seen.add(f.property_key)
      return false
    })
  }, [fields])

  // The control keeps each minimum at or below its maximum, so the only way
  // to reach an invalid span is a stale rule; guard the save anyway.
  const invalidThresholds =
    effectiveAction === 'relevance' &&
    (spans.autoMerge > spans.autoMergeMax || spans.review > spans.reviewMax)

  const busy = create.isPending || update.isPending
  const canSave =
    !busy && Boolean(name.trim()) && fields.length > 0 && !duplicate && !invalidThresholds

  function setField(index: number, patch: Partial<MatchField>) {
    setFields((list) => list.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  }

  /** The next property nobody has picked yet, so "Add" never starts invalid. */
  function nextFreeProperty(): string {
    const used = new Set(fields.map((f) => f.property_key))
    return detail.properties.find((p) => !used.has(p.key))?.key ?? ''
  }

  const addable = detail.properties.length > fields.length

  async function save() {
    const body = {
      // Derived from the name: the key only ever mattered to the API.
      key: name.trim(),
      name: name.trim(),
      fields,
      action: effectiveAction,
      auto_merge_threshold: spans.autoMerge,
      auto_merge_max: spans.autoMergeMax,
      review_threshold: spans.review,
      review_max: spans.reviewMax,
    }
    try {
      if (existing) await update.mutateAsync({ id: existing.match_rule_id, body })
      else await create.mutateAsync(body)
      toast.success('Match rule saved.')
      onClose()
    } catch (error) {
      toast.error(error)
    }
  }

  return (
    <Modal
      title={existing ? `Edit ${existing.name}` : 'New match rule'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={!canSave}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="small muted" style={{ margin: '-4px 0 4px' }}>
          Find records that describe the same thing, by comparing the fields below exactly or by
          similarity.
        </p>

        <Field label="Name" required>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Same domain, similar name"
          />
        </Field>

        {/* The fields are the rule. Each one is compared exactly or fuzzily,
            and adding a second is how a rule gets more than one piece of
            evidence to weigh. */}
        <Field
          label="Fields"
          required
          help="An incoming record is compared to the ones already in the graph on these fields. Every field counts the same."
        >
          <div className="stack" style={{ gap: 6 }}>
            {fields.map((field, index) => (
              <div className="row-tight" key={index}>
                <select
                  className="select"
                  style={{ flex: 2 }}
                  value={field.property_key}
                  aria-label={`Field ${index + 1}`}
                  onChange={(e) => setField(index, { property_key: e.target.value })}
                >
                  {detail.properties.map((property) => (
                    <option key={property.property_id} value={property.key}>
                      {property.key}
                    </option>
                  ))}
                </select>
                <select
                  className="select"
                  style={{ flex: 1 }}
                  value={field.method}
                  aria-label={`How ${field.property_key || `field ${index + 1}`} is compared`}
                  onChange={(e) => setField(index, { method: e.target.value as MatchMethod })}
                >
                  {METHODS.map((m) => (
                    <option key={m.method} value={m.method}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="cp-parr"
                  data-danger="true"
                  aria-label={`Remove ${field.property_key || `field ${index + 1}`}`}
                  disabled={fields.length === 1}
                  onClick={() => setFields((list) => list.filter((_, i) => i !== index))}
                >
                  ✕
                </button>
              </div>
            ))}

            <div className="row-tight">
              <button
                type="button"
                className="btn btn-sm"
                disabled={!addable}
                title={addable ? undefined : 'Every property is already in this rule.'}
                onClick={() =>
                  setFields((list) => [
                    ...list,
                    { property_key: nextFreeProperty(), method: 'exact' },
                  ])
                }
              >
                + Add field
              </button>
              <span className="small faint">
                {METHODS.find((m) => m.method === (fuzzy ? 'fuzzy' : 'exact'))?.summary}
              </span>
            </div>

            {duplicate && (
              <p className="field-error">
                The same field is listed twice. Each field can only appear once in a rule.
              </p>
            )}
          </div>
        </Field>

        {/* What a match is *for*. Relevance — let the score pick between
            merging and reviewing — is the only thing a fuzzy rule can mean, so
            once any field is fuzzy it is the only option shown. */}
        <Field
          label="When two records match"
          help={
            fuzzy
              ? 'This rule compares a field by similarity, so it produces a score rather than a yes or no. Only relevance can read a score.'
              : effectiveAction === 'auto_merge'
                ? 'Matching pairs are merged with nobody watching.'
                : effectiveAction === 'review'
                  ? 'Matching pairs always wait in the review queue.'
                  : 'The score decides between merging, reviewing and leaving the records alone.'
          }
        >
          <div className="cp-survgrid">
            {offeredActions.map((a) => (
              <button
                key={a.action}
                type="button"
                className="cp-sg"
                data-on={effectiveAction === a.action}
                aria-pressed={effectiveAction === a.action}
                onClick={() => setAction(a.action)}
              >
                <div className="sgi" aria-hidden="true">
                  {a.icon}
                </div>
                <div className="sgn">{a.label}</div>
                <div className="sgd">{a.summary}</div>
              </button>
            ))}
          </div>
        </Field>

        {/* Two bars, each a span of 0–1 with a handle at both ends. A minimum
            cannot pass its maximum, so the state this control can be left in
            is always a state the API accepts. */}
        {effectiveAction === 'relevance' && (
          <Field
            label="Which scores lead to which outcome"
            help="A fuzzy comparison produces a score from 0 to 1. Each bar picks the range of scores that triggers its outcome."
          >
            <RelevanceTrack {...spans} onChange={setSpans} />
          </Field>
        )}
      </div>
    </Modal>
  )
}
